import { addTemporaryRole, type TemporaryRoleWrite } from "@/api/access/temporaryRoles";
import { saveEntityScope, type ScopeWrite } from "@/api/access/userScope";
import type { UserScopeCompany } from "@/api/access/userScope.types";
import { createUser } from "@/api/user-management/userManagement.mutations";
import type { CreateUserPayload } from "@/api/user-management/userManagement.mutations.types";
import type { CustomAxiosRequestConfig } from "@/types/common.types";

export type NamedTemporaryRole = TemporaryRoleWrite & {
  roleName: string;
  /** An entity's own copy is given in that entity, where the route looks it up (UI26). */
  entityId?: string | null;
};

export type EntityScopePlan = { company: UserScopeCompany; writes: ScopeWrite[] };

/** What did not land after the person was created. Empty lists mean everything saved. */
export type FollowUpFailures = {
  temporaryRoles: NamedTemporaryRole[];
  entities: EntityScopePlan[];
};

export const nothingFailed = (failures: FollowUpFailures) =>
  failures.temporaryRoles.length === 0 && failures.entities.length === 0;

/**
 * Temporary roles first: a box a temporary role puts in play is refused until that role is held.
 * An entity that fails keeps only its unwritten requests, so a retry never repeats a write.
 */
export async function saveFollowUps(
  userId: string,
  temporaryRoles: NamedTemporaryRole[],
  entities: EntityScopePlan[],
): Promise<FollowUpFailures> {
  const failedRoles: NamedTemporaryRole[] = [];
  for (const role of temporaryRoles) {
    try {
      await addTemporaryRole(
        userId,
        { roleId: role.roleId, endsOn: role.endsOn },
        role.entityId ?? undefined,
      );
    } catch {
      failedRoles.push(role);
    }
  }

  const failedEntities: EntityScopePlan[] = [];
  for (const entity of entities) {
    if (entity.writes.length === 0) continue;
    const result = await saveEntityScope(userId, entity.company, entity.writes);
    if (result.failed.length > 0) {
      failedEntities.push({ company: entity.company, writes: result.failed });
    }
  }

  return { temporaryRoles: failedRoles, entities: failedEntities };
}

/**
 * The person, their entities and permanent roles go in one call, which the backend runs in one
 * transaction. Temporary roles and scope have their own routes, which need the person to exist.
 */
export type NewUserSave = {
  payload: CreateUserPayload;
  temporaryRoles: NamedTemporaryRole[];
  entities: EntityScopePlan[];
};

export async function createUserWithAccess({
  payload,
  temporaryRoles,
  entities,
}: NewUserSave): Promise<{ userId: string; failures: FollowUpFailures }> {
  // Quiet: a taken email belongs on the Email field and any other failure on the page, not a toast.
  const created = await createUser({ ...payload, temporaryRoles: [], tenantExclusions: [] }, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  const userId = created.data.id;
  const failures = await saveFollowUps(userId, temporaryRoles, entities);
  return { userId, failures };
}
